import {FC, useCallback, useRef, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {Box, Breadcrumbs, Button, Typography} from "@mui/material";
import {Link, useParams} from "react-router-dom";
import {useGetLesson} from "@/app2/shared/data";
import {AdminHeader} from "@/pages/ManagerView/AdminHeader";
import {convertSecondsToHHMMSS} from "@/utils";
import type {VideoPlayerRef} from "@buildwitt/shapeshift-common/v2/videoPlayer/videoPlayer";
import {Player, PlayerAPI} from "bitmovin-player/modules/bitmovinplayer-core";
import EngineBitmovinModule from "bitmovin-player/modules/bitmovinplayer-engine-bitmovin";
import MseRendererModule from "bitmovin-player/modules/bitmovinplayer-mserenderer";
import HlsModule from "bitmovin-player/modules/bitmovinplayer-hls";
import DashModule from "bitmovin-player/modules/bitmovinplayer-dash";
import AbrModule from "bitmovin-player/modules/bitmovinplayer-abr";
import XmlModule from "bitmovin-player/modules/bitmovinplayer-xml";
import ContainerTSModule from "bitmovin-player/modules/bitmovinplayer-container-ts";
import ContainerMp4Module from "bitmovin-player/modules/bitmovinplayer-container-mp4";
import SubtitlesModule from "bitmovin-player/modules/bitmovinplayer-subtitles";
import SubtitlesCEA608Module from "bitmovin-player/modules/bitmovinplayer-subtitles-cea608";
import PolyfillModule from "bitmovin-player/modules/bitmovinplayer-polyfill";
import StyleModule from "bitmovin-player/modules/bitmovinplayer-style";
import "bitmovin-player/bitmovinplayer-ui.css";
import {UIFactory} from "bitmovin-player-ui";
import {bitmovinPlayerConfig} from "@/constants";
import { App2LessonVideoPlayer } from "@/app2/pages/ModulePage/LessonPage/LessonVideoPlayer";

interface IStyleProps {
	hasVideo?: boolean;
}

const useStyles = makeStyles<IStyleProps>()((theme, {hasVideo}) => ({
	adminLessonLandingPage: {},
	adminBanner: {
		position: "relative",
		display: "flex",
		flexDirection: "column",
		width: "100%",
		height: "auto",
		borderRadius: "5px",
		overflow: "hidden",
		marginBottom: "12px"
	},
	adminBannerTitle: {
		position: "relative",
		fontSize: "34px",
		lineHeight: "41px",
		fontWeight: "600",
		letterSpacing: "0.25px",
		alignSelf: "end",
		margin: "32px 0 20px",
		zIndex: 2
	},
	adminVideo: {
		width: "100%",
		height: "100%"
	},
	adminBackgroundImage: {
		position: "absolute",
		left: 0,
		top: 0,
		width: "100%",
		height: "100%",
		objectFit: "cover"
	},
	contentBox: {
		marginTop: theme.spacing(3),
		padding: theme.spacing(2.5),
		backgroundColor: theme.palette.background.paper,
		borderWidth: theme.borderWidth?.small ?? 1,
		borderColor: theme.palette.grey[100],
		borderStyle: "solid",
		borderRadius: (theme.borderRadius as any)?.small ?? 4
	},
	contentContainer: {
		display: "flex",
		gap: theme.spacing(3),
		flexDirection: "column",
		"& span": {
			fontWeight: 700
		}
	},
	titleRow: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "space-between"
	},
	hr: {
		borderTop: 0,
		borderColor: theme.palette.grey[100]
	}
}));

/**
 * Admin Lesson Landing Page (App2)
 *
 * View lesson details; same layout/styles as original.
 * Works for both /admin/lessons/:lessonId and /admin/courses/:id/lessons/:lessonId (id from params).
 */
export const AdminLessonLandingPage: FC = () => {
	const {lessonId, id: courseIdParam} = useParams<{lessonId: string; id?: string}>();
	const entityId = lessonId ? parseInt(lessonId, 10) : undefined;
	const courseId = courseIdParam ? parseInt(courseIdParam, 10) : undefined;

	const {data: lesson, isLoading} = useGetLesson(entityId);
	const videoUrl = lesson?.videoUrl;
	const hasVideo = !!videoUrl;
	const {classes} = useStyles({hasVideo});
	const [player, setPlayer] = useState<PlayerAPI | null>(null);
	const videoRef = useRef<VideoPlayerRef | null>(null);
	const playerRef = useRef<PlayerAPI | null>(null);
	playerRef.current = player;	

	const handleVideoReady = useCallback(() => {
		if (!lesson) return;
		if (!process.env.REACT_APP_BITMOVIN_PLAYER_KEY)
			throw new Error("REACT_APP_BITMOVIN_PLAYER_KEY required");

		if (!videoRef.current?.bitmovin?.container)
			return;

		// Destroy existing player before creating new one (fixes black video when navigating back)
		if (playerRef.current) {
			try {
				playerRef.current.pause?.();
				playerRef.current.destroy();
			} catch {
				// ignore
			}
		}

		const playerSource = {
			hls: lesson.videoUrl || "",
			poster: lesson.videoThumbnailUrl || undefined
		};

		Player.addModule(EngineBitmovinModule);
		Player.addModule(MseRendererModule);
		Player.addModule(HlsModule);
		Player.addModule(XmlModule);
		Player.addModule(DashModule);
		Player.addModule(AbrModule);
		Player.addModule(ContainerTSModule);
		Player.addModule(ContainerMp4Module);
		Player.addModule(SubtitlesModule);
		Player.addModule(SubtitlesCEA608Module);
		Player.addModule(PolyfillModule);
		Player.addModule(StyleModule);

		// Delay player setup for Safari
		setTimeout(() => {
			if (!videoRef.current?.bitmovin?.container)
				return;

			const playerInstance = new Player(videoRef.current.bitmovin?.container, bitmovinPlayerConfig);
			UIFactory.buildDefaultUI(playerInstance);
			playerInstance.load(playerSource).then(() => {
				setPlayer(playerInstance);
			}, (e) => {
				console.error("Error while loading source", e);
			});
		}, 100); // Delay added
	}, [lesson?.videoUrl, lesson?.videoThumbnailUrl, lessonId]);	

	if (isLoading || (entityId && !lesson)) {
		return (
			<Box className={classes.adminLessonLandingPage}>
				<Typography>Loading...</Typography>
			</Box>
		);
	}

	const editPath = courseId
		? `/admin/courses/${courseId}/lessons/${entityId}/edit-new`
		: `/admin/lessons/${entityId}/edit-new`;
	const listPath = courseId ? `/admin/courses/${courseId}` : "/admin/lessons";
	const lessonPath = courseId
		? `/admin/courses/${courseId}/lessons/${entityId}`
		: `/admin/lessons/${entityId}`;

	return (
		<Box className={classes.adminLessonLandingPage}>
			<Breadcrumbs separator="/" aria-label="breadcrumb">
				{courseId ? (
					<Link to="/admin/courses">
						<Typography variant="body1">Courses</Typography>
					</Link>
				) : (
					<Link to="/admin/lessons">
						<Typography variant="body1">Lessons</Typography>
					</Link>
				)}
				{courseId && (
					<Link to={`/admin/courses/${courseId}`}>
						<Typography variant="body1">Course</Typography>
					</Link>
				)}
				<Link to={lessonPath}>
					<Typography variant="body1">{lesson?.title}</Typography>
				</Link>
			</Breadcrumbs>

			<Typography variant="h3" className={classes.adminBannerTitle}>
				{lesson?.title}
			</Typography>

			<Box className={classes.adminBanner}>
				{hasVideo && (
					<Box className={classes.adminVideo}>
						<App2LessonVideoPlayer
							key={lessonId ?? ""}
							onReady={handleVideoReady}
							ref={videoRef}
							lesson={lesson}
							bitmovinPlayer={player}
							onVideoFinished={() => {}}
							onPlay={() => {}}
							onPause={() => {}}
							onEnded={() => {}}
							onProgress={() => {}}
							onPlaybackTime={() => {}}
						/>						
					</Box>
				)}
			</Box>

			<Box className={classes.contentBox}>
				<Box className={classes.titleRow}>
					<AdminHeader title="Lesson Information" />
					<Link to={editPath}>
						<Button variant="contained">EDIT LESSON</Button>
					</Link>
				</Box>
				<Box className={classes.contentContainer}>
					{lesson && (
						<>
							{lesson.subtitle && (
								<Typography variant="body1">{lesson.subtitle}</Typography>
							)}
							<hr className={classes.hr} />
							<Typography variant="body1">
								<span>Duration:</span>{" "}
								{lesson.videoDuration != null
									? convertSecondsToHHMMSS(lesson.videoDuration)
									: ""}
							</Typography>
							<Typography variant="body1">
								<span>Last Modified:</span>{" "}
								{lesson.updatedAt ? new Date(lesson.updatedAt).toLocaleString() : ""}
							</Typography>
						</>
					)}
				</Box>
			</Box>
		</Box>
	);
};
